import { useTheme } from "@emotion/react";
import { Box, Stack } from "@mui/material";
import React, { useLayoutEffect, useRef, useState } from "react";

const getMaxItemsCountInRow = (availableWidth, minItemWidth, gapPx) => {
  let itemsInRow = 1;
  while (true) {
    const nextItemsInRow = itemsInRow + 1;
    const rowWidthWithNextItem =
      nextItemsInRow * minItemWidth + (nextItemsInRow - 1) * gapPx;
    if (rowWidthWithNextItem > availableWidth) {
      break;
    } else {
      itemsInRow = nextItemsInRow;
    }
  }
  return itemsInRow;
};

const getBalancedRows = (items, rowsCount) => {
  let rows = Array.from({ length: rowsCount }, () => []);
  let itemsCopy = [...items];
  let i = 0;
  while (itemsCopy.length) {
    rows[i % rowsCount].push(itemsCopy.shift());
    i++;
  }
  return rows;
};

export function SmartWrap({ minItemWidth = 200, gap = 2, sx, children }) {
  const theme = useTheme();
  const containerRef = useRef(null);
  const [availableWidth, setAvailableWidth] = useState(0);

  // ———————————————————— 1. Measure container width ————————————————————
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const observer = new ResizeObserver((entries) => {
      const width =
        entries[0].contentBoxSize?.[0]?.inlineSize ||
        entries[0].contentRect.width;
      setAvailableWidth(width);
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const items = React.Children.toArray(children);
  const itemCount = items.length;
  if (itemCount === 0) return null;

  const gapPx =
    typeof gap === "number" ? parseFloat(theme.spacing(gap)) : parseFloat(gap);

  const maxItemsInRow = getMaxItemsCountInRow(
    availableWidth,
    minItemWidth,
    gapPx,
  );
  const rowsCount = Math.ceil(itemCount / maxItemsInRow);

  const rows = getBalancedRows(items, rowsCount);

  // ———————————————————— 5. Render rows ————————————————————
  return (
    <Stack
      ref={containerRef}
      direction="column"
      gap={gap}
      sx={{ width: "100%", ...sx }}
    >
      {rows.map((rowItems, rowIndex) => {
        const itemsInRow = rowItems.length;
        // const itemFlexBasis = `${100 / itemsInRow}%`;
        const itemFlexBasis = "100%";

        return (
          <Box
            key={rowIndex}
            sx={{
              display: "flex",
              gap: gap,
              width: "100%",
            }}
          >
            {rowItems.map((child, idx) => (
              <Box
                key={idx}
                sx={{
                  flex: `1 1 ${itemFlexBasis}`,
                  minWidth: minItemWidth,
                  display: "flex",
                }}
              >
                {child}
              </Box>
            ))}
          </Box>
        );
      })}
    </Stack>
  );
}
